'use client'
import { useRef, useState, useEffect, useCallback, type RefObject } from 'react';
import { css, cx } from '@mildang/styled-system/css';
import type { SystemStyleObject } from '@mildang/styled-system/types';

interface UseMiddleTruncateParams {
  text: string;
}

interface UseMiddleTruncateReturn {
  containerRef: RefObject<HTMLSpanElement | null>;
  measureRef: RefObject<HTMLSpanElement | null>;
  displayText: string;
  isTruncated: boolean;
}

function useMiddleTruncate({ text }: UseMiddleTruncateParams): UseMiddleTruncateReturn {
  const containerRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);
  const [displayText, setDisplayText] = useState(text);
  const [isTruncated, setIsTruncated] = useState(false);

  const truncateMiddle = useCallback(() => {
    const container = containerRef.current;
    const textEl = measureRef.current;
    if (!container || !textEl) return;

    textEl.textContent = text;

    const containerWidth = container.offsetWidth;
    const textWidth = textEl.scrollWidth;

    if (textWidth <= containerWidth) {
      setDisplayText(text);
      setIsTruncated(false);
      return;
    }

    const ellipsis = '...';
    let start = 0;
    let end = text.length;
    let result = text;

    while (start < end) {
      const mid = Math.floor((start + end) / 2);
      const frontChars = Math.ceil(mid / 2);
      const backChars = Math.floor(mid / 2);

      const truncated = text.slice(0, frontChars) + ellipsis + text.slice(text.length - backChars);

      textEl.textContent = truncated;

      if (textEl.scrollWidth <= containerWidth) {
        result = truncated;
        start = mid + 1;
      } else {
        end = mid;
      }
    }

    setDisplayText(result);
    setIsTruncated(true);
  }, [text]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    truncateMiddle();

    const resizeObserver = new ResizeObserver(() => {
      truncateMiddle();
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [truncateMiddle]);

  return { containerRef, measureRef, displayText, isTruncated };
}

const rootStyles = css({
  display: 'block',
  overflow: 'hidden',
  width: '100%',
});

export interface MiddleEllipsisProps {
  /** 표시할 전체 텍스트. 컨테이너 너비를 넘으면 이진 탐색으로 앞/뒤 글자 수를 줄여 중간에 "..."을 끼워 넣음. */
  text: string;
  /** 컨테이너(root span)에 병합되는 Panda 스타일 오버라이드. */
  sx?: SystemStyleObject;
  /** 실제 말줄임 적용 여부가 바뀔 때마다 호출(초기 측정 시·리사이즈로 재계산될 때 모두 포함). */
  onTruncatedChange?: (isTruncated: boolean) => void;
}

/**
 * 텍스트가 넘칠 때 중간에 ...을 표시하는 컴포넌트
 * ResizeObserver로 컨테이너 크기 변화 감지
 */
export function MiddleEllipsis({ text, sx, onTruncatedChange }: MiddleEllipsisProps) {
  const { containerRef, measureRef, displayText, isTruncated } = useMiddleTruncate({ text });

  useEffect(() => {
    onTruncatedChange?.(isTruncated);
  }, [isTruncated, onTruncatedChange]);

  return (
    <span
      ref={containerRef}
      className={cx(rootStyles, css(sx))}
    >
      <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
        {text}
      </span>
      <span
        aria-hidden
        ref={measureRef}
        style={{
          whiteSpace: 'nowrap',
          visibility: 'hidden',
          position: 'absolute',
        }}
      >
        {text}
      </span>
      <span aria-hidden style={{ whiteSpace: 'nowrap' }}>
        {displayText}
      </span>
    </span>
  );
}
